import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sprint 12 — Public verify-cert page. Reused by QR-code scans printed on
// physical certificates. No auth required; returns a whitelisted subset of
// the certificate record (no PII beyond name/school/grade).
export default function VerifyCert() {
  const { serial } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!serial) return;
    axios.get(`${API}/verify-cert/${serial}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Could not verify certificate'));
  }, [serial]);

  const verifyUrl = `https://geniusoneplus.com/verify-cert/${serial}`;

  const shareToWhatsApp = () => {
    const msg = encodeURIComponent(
      `My child just earned this Genius One Plus certificate! 🏆\n` +
      `Verified real: ${verifyUrl}\n\n` +
      `Genius One Plus — Grade 5-6 transition prep, done right.\n` +
      `Free lesson: https://geniusoneplus.com/try`,
    );
    // Fire-and-forget share-tracking ping. Never blocks the redirect.
    axios.post(`${API}/verify-cert/${serial}/track-share`).catch(() => {});
    window.open(`https://wa.me/?text=${msg}`, '_blank', 'noopener');
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(verifyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) { /* clipboard blocked — ignore */ }
  };

  if (error) return <div className="p-8 text-red-600" data-testid="verify-error">{error}</div>;
  if (!data) return <div className="p-8 text-sm text-slate-500" data-testid="verify-loading">Verifying...</div>;

  const REASONS = {
    bad_serial_format: 'This serial number does not match our format.',
    not_found: 'This certificate serial was not found on record.',
    checksum_mismatch: 'This serial appears tampered with. Verification failed.',
  };

  if (!data.valid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
           data-testid="verify-cert-invalid-page">
        <Card className="max-w-lg w-full border-2 border-red-300">
          <CardHeader>
            <CardTitle className="text-2xl text-red-700">Not verified</CardTitle>
            <CardDescription>
              We could not verify certificate <span className="font-mono">{serial}</span>.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-slate-700 space-y-3">
            <p data-testid="verify-cert-reason">{REASONS[data.reason] || 'Unknown reason.'}</p>
            <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="verify-cert-try-cta">
              <Link to="/try">Try a free lesson &rarr;</Link>
            </Button>
            <Link to="/" className="text-emerald-700 underline block text-center pt-2">
              &larr; Back to Genius One Plus
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const awarded = data.awarded_at ? new Date(data.awarded_at) : null;
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-white flex flex-col items-center justify-center px-4 py-10"
         data-testid="verify-cert-valid-page">
      <img
        src="/brand/crest_flat.png"
        alt="Genius One Plus crest"
        className="h-20 w-auto mb-4"
        data-testid="verify-brand-crest"
      />
      <Card className="max-w-2xl w-full border-2 border-emerald-400 shadow-xl">
        <CardHeader>
          <div className="flex justify-between items-start flex-wrap gap-3">
            <div>
              <CardTitle className="text-3xl text-emerald-700 flex items-center gap-2">
                <span data-testid="verify-cert-tick">🏆</span>
                <span>Verified</span>
              </CardTitle>
              <CardDescription>
                Certificate issued by Genius One Plus, a programme of TEC Sri Lanka Worldwide (Pvt) Ltd.
              </CardDescription>
            </div>
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300"
                   data-testid="verify-cert-tier">
              {data.tier_label || data.tier}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 text-slate-800">
          <p className="text-lg" data-testid="verify-cert-summary-line">
            This certificate belongs to{' '}
            <span className="font-bold text-2xl" data-testid="verify-cert-student-name">
              {data.student_name}
            </span>{', '}
            <span data-testid="verify-cert-grade-school">
              Grade {data.grade}, {data.school}
            </span>.
          </p>
          <div className="grid grid-cols-2 gap-3 text-sm pt-2">
            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Subject</p>
              <p className="font-semibold" data-testid="verify-cert-subject">
                {data.subject_label || data.subject}
              </p>
            </div>
            <div className="rounded-md border border-slate-200 p-3">
              <p className="text-xs text-slate-500">Awarded</p>
              <p className="font-semibold" data-testid="verify-cert-awarded-date">
                {awarded ? awarded.toLocaleDateString('en-GB',
                  { year: 'numeric', month: 'long', day: '2-digit' }) : '\u2014'}
              </p>
            </div>
            {data.average_score != null && (
              <div className="rounded-md border border-slate-200 p-3 col-span-2">
                <p className="text-xs text-slate-500">Average score</p>
                <p className="font-semibold" data-testid="verify-cert-avg-score">
                  {data.average_score}%
                </p>
              </div>
            )}
          </div>
          <div className="pt-4 space-y-2">
            <Button onClick={shareToWhatsApp}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-lg py-6"
                    data-testid="verify-cert-share-wa-btn">
              📱 Share on WhatsApp
            </Button>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button asChild variant="outline" className="flex-1"
                      data-testid="verify-cert-download-pdf">
                <a href={`${API}/certificate/${data.certificate_id}.pdf`} target="_blank" rel="noreferrer">
                  Download PDF
                </a>
              </Button>
              <Button onClick={copyLink} variant="outline" className="flex-1"
                      data-testid="verify-cert-copy-link-btn">
                {copied ? '✓ Link copied' : 'Copy certificate link'}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 pt-3 font-mono" data-testid="verify-cert-serial">
            Serial: {serial}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
